import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { CustomValidators } from "app/pages/A2-utils/custom-validators";
import { Subscription } from "rxjs/Subscription";
import { QuizAnswerClass, QuizClass } from "../quiz.class";
import { QuizAnswerFormValue } from "../quiz-form-values.interface";
import { QuizService } from "../quiz.service";
import { T2LoadingService } from "app/trip2-theme/components/t2-loading/t2-loading.service";
import { SweetAlertHelper } from "app/core/helpers/sweet-alert-helper.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "app-quiz-answer-form",
  templateUrl: "./quiz-answer-form.component.html",
})
export class QuizAnswerFormComponent implements OnInit, OnDestroy {
  quizClass: QuizClass;
  quizAnswerForm: FormGroup;
  private questionIndex: number = null;
  private answerIndex: number = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private _serviceQuiz: QuizService,
    private sweetAlertHelper: SweetAlertHelper,
    private loading: T2LoadingService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  private initForm(): void {
    this.quizAnswerForm = this.fb.group({
      quizQuestionId: [null, [Validators.required]],
      quizAnswerId: [null],
      description: [null, [CustomValidators.required]],
      isCorrect: [false],
    });
  }

  private initSubscriptions(): void {
    this.subscribeToQuizData();
    this.subscribeToAnswerFormAction();
  }

  private subscribeToQuizData(): void {
    this.subscriptions.push(
      this._serviceQuiz.quizData$.subscribe({
        next: (data) => {
          if (data) {
            this.quizClass = data;
          }
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  private subscribeToAnswerFormAction(): void {
    this.subscriptions.push(
      this._serviceQuiz.answerFormAction$.subscribe({
        next: (action) => {
          if (!action) {
            return;
          }
          this.handleAnswerFormAction(action);
        },
        error: (err) => {
          this.sweetAlertHelper.captureException(err);
        },
      })
    );
  }

  private handleAnswerFormAction(action: any): void {
    if (action.mode === "new") {
      this.openNew(action.questionIndex);
    } else if (action.mode === "edit" && action.data && action.answerIndex !== undefined) {
      this.openEdit(action.data, action.answerIndex, action.questionIndex);
    }
  }

  openNew(questionIndex: number): void {
    if (!this.isValidQuestionIndex(questionIndex)) {
      return;
    }

    this.questionIndex = questionIndex;
    this.answerIndex = null;
    const questionData = this.quizClass.quizquestion[questionIndex];
    this.quizAnswerForm.patchValue({
      quizQuestionId: questionData.quizquestionid,
      description: null,
      isCorrect: false,
    });
  }

  get disabledSaveButton(): boolean {
    return !(this.quizAnswerForm && this.quizAnswerForm.valid);
  }

  openEdit(data: QuizAnswerClass, answerIndex: number, questionIndex: number): void {
    if (!this.quizClass) {
      return;
    }

    this.questionIndex = questionIndex;
    this.answerIndex = answerIndex;

    this.quizAnswerForm.patchValue({
      quizQuestionId: data.quizquestionid,
      quizAnswerId: data.quizanswerid,
      description: data.description,
      isCorrect: data.iscorrect,
    });
  }

  private isValidQuestionIndex(questionIndex: number): boolean {
    if (!this.quizClass || !this.quizClass.quizquestion) {
      return false;
    }
    return questionIndex >= 0 && questionIndex < this.quizClass.quizquestion.length;
  }

  private mapQuizAnswerFormToApi(form: QuizAnswerFormValue): QuizAnswerClass {
    const m = new QuizAnswerClass();
    m.quizquestionid = form.quizQuestionId;
    m.quizanswerid = form.quizAnswerId;
    m.description = form.description;
    m.iscorrect = form.isCorrect;
    return m;
  }

  submitFormAnswer(): void {
    if (!this.canSubmitForm()) {
      return;
    }

    const form = this.mapQuizAnswerFormToApi(this.quizAnswerForm.value as QuizAnswerFormValue);

    if (!this.validateCorrectAnswer(form)) {
      return;
    }

    if (this.answerIndex !== null) {
      this.updateFormAnswer(form);
    } else {
      this.newFormAnswer(form);
    }
  }

  private canSubmitForm(): boolean {
    if (!this.quizClass || !this.quizClass.quizquestion) {
      return false;
    }
    if (!this.quizAnswerForm.valid) {
      return false;
    }
    return this.isValidQuestionIndex(this.questionIndex);
  }

  private validateCorrectAnswer(form: QuizAnswerClass): boolean {
    if (!form.iscorrect) {
      return true;
    }

    const question = this.quizClass.quizquestion[this.questionIndex];
    const rows = (question && question.quizanswer) || [];
    const existCorrect = rows.some(
      (sel) => sel.iscorrect && sel.quizanswerid !== form.quizanswerid
    );

    if (existCorrect) {
      this.sweetAlertHelper.createCustomAlert({
        type: "warning",
        title: "Oops",
        text: "Disable one of the current answers as correct",
      });
      return false;
    }

    return true;
  }

  private newFormAnswer(form: QuizAnswerClass): void {
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
        .postQuizAnswer(form)
        .pipe(finalize(() => this.loading.hiden()))
        .subscribe({
          next: (res: QuizAnswerClass) => {
            if (res) {
              this.handleNewAnswerSuccess(res, form);
            }
          },
          error: (err) => {
            this.sweetAlertHelper.captureException(err);
          },
        })
    );
  }

  private handleNewAnswerSuccess(res: QuizAnswerClass, form: QuizAnswerClass): void {
    this._serviceQuiz.addAnswerToQuestion(this.questionIndex, {
      quizanswerid: res.quizanswerid,
      quizquestionid: res.quizquestionid,
      description: form.description,
      iscorrect: form.iscorrect,
    });
    this.resetAfterSuccess();
  }

  private updateFormAnswer(form: QuizAnswerClass): void {
    this.loading.show();
    this.subscriptions.push(
      this._serviceQuiz
        .putQuizAnswer(form)
        .pipe(finalize(() => this.loading.hiden()))
        .subscribe({
          next: (res: QuizAnswerClass) => {
            if (res) {
              this.handleUpdateAnswerSuccess(form);
            }
          },
          error: (err) => {
            this.sweetAlertHelper.captureException(err);
          },
        })
    );
  }

  private handleUpdateAnswerSuccess(form: QuizAnswerClass): void {
    this._serviceQuiz.updateAnswerInQuestion(
      this.questionIndex,
      this.answerIndex,
      form.description,
      form.iscorrect
    );
    this.resetAfterSuccess();
  }

  private resetAfterSuccess(): void {
    this.quizAnswerForm.reset();
    this.questionIndex = null;
    this.answerIndex = null;
    this._serviceQuiz.setAnswerFormSaved(true);
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      });
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/pages/quiz/quiz-answer-form/quiz-answer-form.component.ts